import Big from 'big.js';
import dayjs, { Dayjs } from 'dayjs';
import { FormikProvider, useFormik } from 'formik';
import { isNumber } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import * as yup from 'yup';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import { RegisterBanksSelect } from '@/modules/common/components/RegisterBanksSelect.tsx';
import { BigRoundMode } from '@/modules/common/constants/big-round-mode.ts';
import { useQuantityPricePerShareTotal } from '@/modules/common/hooks/useQuantityPricePerShareTotal.ts';
import { SelectAsset } from '@/modules/issuer-dashboard/components/portfolios/SelectAsset.tsx';
import { SelectPortfolioBrokers } from '@/modules/issuer-dashboard/components/portfolios/SelectPortfolioBrokers.tsx';
import { mapPortfolioTransactionTypeBookToSettled } from '@/modules/issuer-dashboard/components/portfolios/SelectPortfolioTransactions.tsx';
import { formatPricePerShareForInput } from '@/modules/issuer-dashboard/components/portfolios/formatPricePerShareForInput.ts';
import {
  useIssuerAddPortfolioTransactionMutation,
  useIssuerCurrencyHistoricalFxRateQuery,
  useIssuerUpdatePortfolioTransactionMutation,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import {
  BaseModalProps,
  Button,
  CalendarInput,
  CalendarInputProps,
  CustomCheckbox,
  ModalContentWrapper,
  ModalProviderContext,
  Select,
  SelectPaginateOption,
  Spinner,
  TextInputField,
  TooltipNew,
  getSelectOptionByEnum,
} from 'ui';
import { fixNameEnum, getDisableTime, goodFormatNumber, goodFormatWithCurrency } from 'ui/utils';
import {
  AssetExtended,
  BrokerBalances,
  BrokerExtended,
  CreatePortfolioTransactionRequest,
  CreatePortfolioTransactionResponse,
  CurrencyEnum,
  PortfolioTransactionExtended,
  PortfolioTransactionStatus,
  PortfolioTransactionType,
  RegisterBankExtended,
} from 'api';

enum SplitType {
  Forward = 'forward',
  Reverse = 'reverse',
}

interface CreatePortfolioTransactionRequestImprove extends CreatePortfolioTransactionRequest {
  total: number;
  totalBank: number;
  splitType?: SplitType;
}

export const listTypeWithSell = [
  PortfolioTransactionType.TradeSellSettle,
  PortfolioTransactionType.TradeSellBook,
];

export const listTypeWithBuy = [
  PortfolioTransactionType.TradeBuySettle,
  PortfolioTransactionType.TradeBuyBook,
];

const listTypeWithBook = [
  PortfolioTransactionType.TradeBuyBook,
  PortfolioTransactionType.TradeSellBook,
];
const listTypeWithSettle = [
  PortfolioTransactionType.TradeBuySettle,
  PortfolioTransactionType.TradeSellSettle,
];

const listTypeWithPrice = [
  ...listTypeWithSell,
  ...listTypeWithBuy,
  PortfolioTransactionType.IncomeDividendReinvestment,
  PortfolioTransactionType.Split,
  PortfolioTransactionType.Revaluation,
];

const listTypeWithQuantity = [
  ...listTypeWithSell,
  ...listTypeWithBuy,
  PortfolioTransactionType.IncomeDividendReinvestment,
  PortfolioTransactionType.Split,
];

const listTypeWithBrokerType = [
  PortfolioTransactionType.BrokerDeposit,
  PortfolioTransactionType.BrokerWithdraw,
  PortfolioTransactionType.BrokerCurrencyConversion,
];

const listTypeWithBroker = [...listTypeWithSell, ...listTypeWithBuy, ...listTypeWithBrokerType];

const listTypeWithAmount = [
  PortfolioTransactionType.IncomeCashInterest,
  PortfolioTransactionType.IncomeDividendCash,
  PortfolioTransactionType.IncomeRental,
  PortfolioTransactionType.Fee,
  ...listTypeWithBrokerType,
];

const listTypeWithToBank = [
  ...listTypeWithSell,
  PortfolioTransactionType.IncomeRental,
  PortfolioTransactionType.IncomeDividendCash,
  PortfolioTransactionType.IncomeCashInterest,
  PortfolioTransactionType.BrokerWithdraw,
];

const listTypeWithFromBank = [
  ...listTypeWithBuy,
  PortfolioTransactionType.Fee,
  PortfolioTransactionType.BrokerDeposit,
];

const listTypeWithAsset = [
  ...listTypeWithSell,
  ...listTypeWithBuy,
  PortfolioTransactionType.IncomeDividendReinvestment,
  PortfolioTransactionType.IncomeDividendCash,
  PortfolioTransactionType.IncomeRental,
  PortfolioTransactionType.Revaluation,
  PortfolioTransactionType.Fee,
  PortfolioTransactionType.Split,
];

const listTypeBuy = [
  ...listTypeWithBuy,
  PortfolioTransactionType.BrokerDeposit, //instead BrokerWithdraw
];

const listTypeFxRate = [
  ...listTypeWithBook,
  ...listTypeWithSettle,
  PortfolioTransactionType.BrokerCurrencyConversion,
];

const listTypeWithFeeField = [
  ...listTypeWithSell,
  ...listTypeWithBuy,
  PortfolioTransactionType.IncomeDividendReinvestment,
  PortfolioTransactionType.IncomeDividendCash,
  PortfolioTransactionType.IncomeRental,
  PortfolioTransactionType.IncomeCashInterest,
];

const listTypeWithSplitPostQuantity = [PortfolioTransactionType.Split];

const validationSchema = yup.object<CreatePortfolioTransactionRequestImprove>({
  transaction_type: yup
    .mixed<PortfolioTransactionType>()
    .oneOf(Object.values(PortfolioTransactionType))
    .required('This field is required'),
  // name: yup.string().trim().required('Asset name is required').min(1, 'Asset name is too short'),
  asset_id: yup.number().when(['transaction_type'], ([transaction_type], schema) => {
    return listTypeWithAsset.includes(transaction_type)
      ? schema.moreThan(0, 'This field is required').required('This field is required')
      : schema.nullable();
  }),
  broker_id: yup.number().when(['transaction_type'], ([transaction_type], schema) => {
    if (listTypeWithBuy.includes(transaction_type) || listTypeWithSell.includes(transaction_type)) {
      return schema.nullable();
    }
    return listTypeWithBroker.includes(transaction_type)
      ? schema.moreThan(0, 'This field is required').required('This field is required')
      : schema.nullable();
  }),
  from_currency: yup
    .mixed<CurrencyEnum>()
    .when(['transaction_type'], ([transaction_type], schema) => {
      return PortfolioTransactionType.BrokerCurrencyConversion === transaction_type
        ? schema.oneOf(Object.values(CurrencyEnum)).required('This field is required')
        : schema.nullable();
    }),
  to_currency: yup
    .mixed<CurrencyEnum>()
    .when(['transaction_type'], ([transaction_type], schema) => {
      return PortfolioTransactionType.BrokerCurrencyConversion === transaction_type
        ? schema.oneOf(Object.values(CurrencyEnum)).required('This field is required')
        : schema.nullable();
    }),
  // splitType: yup.string().trim().when(['transaction_type'], ([transaction_type], schema) => {
  //   return listTypeWithSplitPostQuantity.includes(transaction_type)
  //     ? schema.oneOf(Object.values(SplitType)).required('This field is required')
  //     : schema.nullable();
  // }),
  price_per_share: yup.number().when(['transaction_type'], ([transaction_type], schema) => {
    return listTypeWithPrice.includes(transaction_type) ||
      transaction_type === PortfolioTransactionType.Revaluation
      ? schema
          .typeError('Price must be a number')
          .moreThan(0, 'Price must be more than 0')
          .required('This field is required')
      : schema.nullable();
  }),
  quantity: yup.number().when(['transaction_type'], ([transaction_type], schema) => {
    return listTypeWithQuantity.includes(transaction_type)
      ? schema
          .typeError('Quantity must be a number')
          .moreThan(0, 'Quantity must be more than 0')
          .required('This field is required')
      : schema.nullable();
  }),
  fee: yup.number().nullable(),
  amount: yup.number().when(['transaction_type'], ([transaction_type], schema) => {
    return listTypeWithAmount.includes(transaction_type)
      ? schema
          .typeError('Amount must be a number')
          .moreThan(0, 'Amount must be more than 0')
          .required('This field is required')
      : schema.nullable();
  }),
  fx_rate: yup.number().when(['transaction_type'], ([transaction_type], schema) => {
    return listTypeFxRate.includes(transaction_type)
      ? schema.moreThan(0, 'This field is required').required('This field is required')
      : schema.nullable();
  }),
  confirmation_date: yup
    .string()
    .trim()
    .required('This field is required')
    .test('not-in-future', 'Date cannot be in the future', function (value) {
      const { transaction_type } = this.parent;
      const canScheduleFuture =
        listTypeWithBuy.includes(transaction_type) || listTypeWithSell.includes(transaction_type);

      return canScheduleFuture || !value || !dayjs(value).isAfter(dayjs());
    }),
  from_portfolio_bank_id: yup
    .number()
    .when(['transaction_type', 'broker_id'], ([transaction_type, broker_id], schema) => {
      if (listTypeWithBuy.includes(transaction_type)) {
        return !broker_id
          ? schema
              .moreThan(0, 'You need to choose a bank or a broker for the balance')
              .required('You need to choose a bank or a broker for the balance')
          : schema.nullable();
      }
      return listTypeWithFromBank.includes(transaction_type)
        ? schema.moreThan(0, 'This field is required').required('This field is required')
        : schema.nullable();
    }),
  to_portfolio_bank_id: yup
    .number()
    .when(['transaction_type', 'broker_id'], ([transaction_type, broker_id], schema) => {
      if (listTypeWithSell.includes(transaction_type)) {
        return !broker_id
          ? schema
              .moreThan(0, 'You need to choose a bank or a broker for the balance')
              .required('You need to choose a bank or a broker for the balance')
          : schema.nullable();
      }
      return listTypeWithToBank.includes(transaction_type)
        ? schema.moreThan(0, 'This field is required').required('This field is required')
        : schema.nullable();
    }),
  ref_portfolio_transaction_id: yup
    .number()
    .when(['transaction_type'], ([transaction_type], schema) => {
      return listTypeWithSettle.includes(transaction_type)
        ? schema.moreThan(0, 'This field is required').required('This field is required')
        : schema.nullable();
    }),
  // split_post_quantity: yup.number().when(['transaction_type'], ([transaction_type], schema) => {
  //   return listTypeWithSplitPostQuantity.includes(transaction_type)
  //     ? schema.moreThan(0, 'This field is required').required('This field is required')
  //     : schema.nullable();
  // }),
});

export interface IssuerAddUpdatePortfolioTransactionModalProps extends BaseModalProps {
  item?: PortfolioTransactionExtended;
  portfolioId: number;
  isSettle?: boolean;
  submitAfterOpen?: boolean;
}

enum PortfolioTransactionTypeEdit {
  Buy = 'buy',
  Sell = 'sell',
  IncomeDividendReinvestment = 'dividend_distribution_reinvestment',
  IncomeDividendCash = 'dividend_distribution_cash',
  IncomeCashInterest = 'cash_interest',
  IncomeRental = 'rental_income',
  Revaluation = 'revaluation',
  Fee = 'fee',
  Split = 'split',
  BrokerDeposit = 'broker_deposit',
  BrokerWithdraw = 'broker_withdraw',
  BrokerCurrencyConversion = 'broker_currency_conversion',
}

export const mapPortfolioTransactionTypeToEdit: Partial<
  Record<PortfolioTransactionType, PortfolioTransactionTypeEdit>
> = {
  [PortfolioTransactionType.TradeBuyBook]: PortfolioTransactionTypeEdit.Buy,
  [PortfolioTransactionType.TradeSellBook]: PortfolioTransactionTypeEdit.Sell,
  [PortfolioTransactionType.TradeBuySettle]: PortfolioTransactionTypeEdit.Buy,
  [PortfolioTransactionType.TradeSellSettle]: PortfolioTransactionTypeEdit.Sell,
  [PortfolioTransactionType.IncomeDividendReinvestment]:
    PortfolioTransactionTypeEdit.IncomeDividendReinvestment,
  [PortfolioTransactionType.IncomeDividendCash]: PortfolioTransactionTypeEdit.IncomeDividendCash,
  [PortfolioTransactionType.IncomeCashInterest]: PortfolioTransactionTypeEdit.IncomeCashInterest,
  [PortfolioTransactionType.IncomeRental]: PortfolioTransactionTypeEdit.IncomeRental,
  [PortfolioTransactionType.Revaluation]: PortfolioTransactionTypeEdit.Revaluation,
  [PortfolioTransactionType.Fee]: PortfolioTransactionTypeEdit.Fee,
  [PortfolioTransactionType.Split]: PortfolioTransactionTypeEdit.Split,
  [PortfolioTransactionType.BrokerDeposit]: PortfolioTransactionTypeEdit.BrokerDeposit,
  [PortfolioTransactionType.BrokerWithdraw]: PortfolioTransactionTypeEdit.BrokerWithdraw,
  [PortfolioTransactionType.BrokerCurrencyConversion]:
    PortfolioTransactionTypeEdit.BrokerCurrencyConversion,
};

export const mapPortfolioTransactionEditTypeToType: Partial<
  Record<PortfolioTransactionTypeEdit, PortfolioTransactionType>
> = {
  [PortfolioTransactionTypeEdit.Buy]: PortfolioTransactionType.TradeBuyBook,
  [PortfolioTransactionTypeEdit.Sell]: PortfolioTransactionType.TradeSellBook,
  [PortfolioTransactionTypeEdit.IncomeDividendReinvestment]:
    PortfolioTransactionType.IncomeDividendReinvestment,
  [PortfolioTransactionTypeEdit.IncomeDividendCash]: PortfolioTransactionType.IncomeDividendCash,
  [PortfolioTransactionTypeEdit.IncomeCashInterest]: PortfolioTransactionType.IncomeCashInterest,
  [PortfolioTransactionTypeEdit.IncomeRental]: PortfolioTransactionType.IncomeRental,
  [PortfolioTransactionTypeEdit.Revaluation]: PortfolioTransactionType.Revaluation,
  [PortfolioTransactionTypeEdit.Fee]: PortfolioTransactionType.Fee,
  [PortfolioTransactionTypeEdit.Split]: PortfolioTransactionType.Split,
  [PortfolioTransactionTypeEdit.BrokerDeposit]: PortfolioTransactionType.BrokerDeposit,
  [PortfolioTransactionTypeEdit.BrokerWithdraw]: PortfolioTransactionType.BrokerWithdraw,
  [PortfolioTransactionTypeEdit.BrokerCurrencyConversion]:
    PortfolioTransactionType.BrokerCurrencyConversion,
};

export const IssuerAddUpdatePortfolioTransactionModal = ({
  open,
  onClose,
  item,
  portfolioId,
  isSettle = false,
  submitAfterOpen = false,
}: IssuerAddUpdatePortfolioTransactionModalProps) => {
  const ranRef = useRef<boolean>(false);
  const [disableLoadRate, setDisableLoadRate] = useState(!!item);
  const { openModal } = useContext(ModalProviderContext);
  const [type, setType] = useState<PortfolioTransactionTypeEdit | null>(
    item?.transaction_type
      ? mapPortfolioTransactionTypeToEdit[item.transaction_type] || null
      : null,
  );
  const [selectedAsset, setSelectedAsset] = useState<AssetExtended | undefined>(item?.asset);
  const [currencyAsset, setCurrencyAsset] = useState<CurrencyEnum | undefined>(
    item?.asset?.currency,
  );
  const [brokerBalances, setBrokerBalances] = useState<BrokerBalances[]>(
    item?.broker?.balances || [],
  );
  const currentBalanceWithAssetCurrency =
    brokerBalances.find(item => item.currency === currencyAsset) || brokerBalances?.[0];
  const [currencyBank, setCurrencyBank] = useState<CurrencyEnum | undefined>(
    item?.to_bank?.bank_currency ||
      item?.from_bank?.bank_currency ||
      currentBalanceWithAssetCurrency?.currency ||
      currencyAsset,
  );
  const initialAvailableBank = useMemo(() => {
    const currentBankBalance = item?.to_bank?.total_available_cash
      ? +item.to_bank.total_available_cash
      : item?.from_bank?.total_available_cash
      ? +item.from_bank.total_available_cash
      : currentBalanceWithAssetCurrency?.value
      ? +currentBalanceWithAssetCurrency.value
      : 0;

    if (!item?.amount) {
      return currentBankBalance;
    }

    if (item.transaction_type === PortfolioTransactionType.BrokerDeposit && item.from_bank) {
      return currentBankBalance + +item.amount;
    }

    if (item.transaction_type === PortfolioTransactionType.BrokerWithdraw && item.to_bank) {
      return currentBankBalance - +item.amount;
    }

    return currentBankBalance;
  }, [currentBalanceWithAssetCurrency, item]);
  const [availableBank, setAvailableBank] = useState<number | undefined>(initialAvailableBank);
  const [isFromBankBalanceFetching, setIsFromBankBalanceFetching] = useState(false);

  const [isFromFinancialModelingPrep, setIsFromFinancialModelingPrep] = useState(false);

  const [settle, setSettle] = useState(isSettle);
  const totalBankInit = useMemo(() => {
    const total =
      item?.quantity && item?.price_per_share
        ? +item?.price_per_share * +item?.quantity
        : undefined;
    const rate = item?.fx_rate ? +item?.fx_rate : 1;
    const isBuy = item?.transaction_type ? listTypeBuy.includes(item?.transaction_type) : false;

    if (!total || !rate) return availableBank;

    const totalBank = isBuy
      ? Number(
          new Big(total)
            .div(rate || 1)
            .round(2)
            .valueOf(),
        )
      : Number(
          new Big(total)
            .mul(rate || 1)
            .round(2)
            .valueOf(),
        );

    return totalBank;
  }, [item]);
  const brokerBalanceAdjustment = useMemo(() => {
    if (!item?.broker_id || !item.asset || !listTypeWithSettle.includes(item.transaction_type)) {
      return undefined;
    }
    const tradeValue = +item.quantity * +item.price_per_share;
    const fxRate = +(item.fx_rate ?? 1) || 1;
    const fee = +(item.fee ?? 0) || 0;
    const oldCashChange =
      item.transaction_type === PortfolioTransactionType.TradeBuySettle
        ? -(tradeValue + fee) / fxRate
        : (tradeValue - fee) / fxRate;
    return { currency: item.asset.currency, value: -oldCashChange };
  }, [item]);

  const handleOnSubmit = useCallback(
    async (values: CreatePortfolioTransactionRequestImprove) => {
      if (values.from_portfolio_bank_id && isFromBankBalanceFetching) {
        toast.error('Loading the selected bank balance. Please try again in a moment.');
        return;
      }
      const quantity = listTypeWithSplitPostQuantity.includes(values.transaction_type)
        ? (values?.quantity ?? 0) - (selectedAsset?.quantity ?? 0)
        : values?.quantity;
      const split_post_quantity = listTypeWithSplitPostQuantity.includes(values.transaction_type)
        ? values?.quantity
        : undefined;
      const fxRate =
        values.transaction_type === PortfolioTransactionType.BrokerDeposit ||
        values.transaction_type === PortfolioTransactionType.BrokerWithdraw
          ? values.fx_rate ?? 1
          : values.fx_rate;
      const { amount: formAmount, ...transactionValues } = values;
      const amountValues = listTypeWithAmount.includes(values.transaction_type)
        ? { amount: formAmount }
        : {};
      try {
        if (item && !isSettle) {
          await mutateAsyncEdit({
            ...transactionValues,
            ...amountValues,
            fx_rate: fxRate,
            quantity,
            split_post_quantity,
            fee: !values.fee || isNaN(+values.fee) ? undefined : values.fee,
            status: listTypeWithBook.includes(values.transaction_type)
              ? PortfolioTransactionStatus.Pending
              : PortfolioTransactionStatus.Completed,
            id: item.id,
          });
          return;
        }
        await mutateAsyncAdd({
          ...transactionValues,
          ...amountValues,
          fx_rate: fxRate,
          quantity,
          split_post_quantity,
          fee: !values.fee || isNaN(+values.fee) ? undefined : values.fee,
          is_complete_book: settle || undefined,
          status: listTypeWithBook.includes(values.transaction_type)
            ? PortfolioTransactionStatus.Pending
            : PortfolioTransactionStatus.Completed,
        });
      } catch {
        // The API error has already been displayed by withErrorHandling.
      }
    },
    [isFromBankBalanceFetching, isSettle, item, selectedAsset, settle],
  );

  const handleSelectedFromBankChange = useCallback((bank: RegisterBankExtended | null) => {
    setAvailableBank(
      bank?.total_available_cash !== undefined && bank?.total_available_cash !== null
        ? +bank.total_available_cash
        : undefined,
    );
  }, []);

  const formik = useFormik<CreatePortfolioTransactionRequestImprove>({
    validationSchema,
    initialValues: {
      portfolio_id: portfolioId,
      price_per_share: formatPricePerShareForInput(item?.price_per_share),
      asset_id: item?.asset_id || undefined,
      broker_id: item?.broker?.id || undefined,
      from_currency: item?.from_currency || undefined,
      to_currency: item?.to_currency || undefined,
      quantity:
        item?.transaction_type === PortfolioTransactionType.Split && item?.split_post_quantity
          ? +item?.split_post_quantity
          : item?.quantity
          ? +item?.quantity
          : undefined,
      amount: item?.amount ? +item?.amount : undefined,
      fee: item?.fee ? +item?.fee : undefined,
      total:
        item?.transaction_type === PortfolioTransactionType.Split
          ? item?.split_post_quantity && item?.price_per_share
            ? +item?.price_per_share * +item?.split_post_quantity
            : undefined
          : item?.quantity && item?.price_per_share
          ? +item?.price_per_share * +item?.quantity
          : undefined,
      fx_rate: item?.fx_rate ? +item?.fx_rate : undefined,
      totalBank: totalBankInit,
      transaction_type: item?.transaction_type
        ? isSettle
          ? mapPortfolioTransactionTypeBookToSettled[item?.transaction_type]
          : item?.transaction_type
        : '',
      confirmation_date: item?.confirmation_date ? dayjs(item?.confirmation_date) : '',
      to_portfolio_bank_id: item?.to_portfolio_bank_id || undefined,
      from_portfolio_bank_id: item?.from_portfolio_bank_id || undefined,

      ref_portfolio_transaction_id:
        (isSettle ? item?.id : item?.ref_portfolio_transaction_id) || undefined,
      memo: undefined,
      is_complete_book: isSettle ? true : undefined,
      split_post_quantity: item?.split_post_quantity ? +item?.split_post_quantity : undefined,
      splitType:
        item?.transaction_type === PortfolioTransactionType.Split &&
        item.quantity !== undefined &&
        item.quantity !== null
          ? +item.quantity < 0
            ? SplitType.Reverse
            : SplitType.Forward
          : undefined,
    } as unknown as CreatePortfolioTransactionRequestImprove,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: handleOnSubmit,
  });
  const { setErrors } = formik;

  const handleOnSuccess = useCallback(
    (data: CreatePortfolioTransactionResponse) => {
      if (settle && (!item || !isSettle)) {
        onClose();
        openModal(IssuerAddUpdatePortfolioTransactionModal, {
          portfolioId,
          item: data.transaction,
          isSettle: true,
          submitAfterOpen: true,
        });

        return;
      }
      onClose();
    },
    [item, onClose, openModal, portfolioId, settle, isSettle],
  );

  const { mutateAsync: mutateAsyncAdd, isLoading: isLoadingAdd } =
    useIssuerAddPortfolioTransactionMutation({
      setErrors: setErrors,
      onSuccess: handleOnSuccess,
    });

  const { mutateAsync: mutateAsyncEdit, isLoading: isLoadingEdit } =
    useIssuerUpdatePortfolioTransactionMutation({
      setErrors: setErrors,
      onSuccess: handleOnSuccess,
    });

  const {
    isShowAmount,
    isShowQuantity,
    isShowPrice,
    isShowAsset,
    isShowToBank,
    isShowFromBank,
    isShowBroker,
    isShowBook,
    isShowFeeField,
    isRevaluation,
    isShowFxRate,
    isBuy,
    isShowSplit,
    isBrokerCurrencyConversion,
    isBrokerDeposit,
    isBrokerWithdraw,
    isSellType,
    isBuyType,
    isCashInterest,
  } = useMemo(() => {
    const isShowAmount = listTypeWithAmount.includes(formik.values.transaction_type);
    const isShowQuantity = listTypeWithQuantity.includes(formik.values.transaction_type);
    const isShowPrice = listTypeWithPrice.includes(formik.values.transaction_type);
    const isShowAsset = listTypeWithAsset.includes(formik.values.transaction_type);
    const isShowToBank = listTypeWithToBank.includes(formik.values.transaction_type);
    const isShowFromBank = listTypeWithFromBank.includes(formik.values.transaction_type);
    const isShowBroker = listTypeWithBroker.includes(formik.values.transaction_type);
    const isShowBook = listTypeWithBook.includes(formik.values.transaction_type);
    const isShowFeeField = listTypeWithFeeField.includes(formik.values.transaction_type);
    const isRevaluation = PortfolioTransactionType.Revaluation === formik.values.transaction_type;
    const isShowFxRate = listTypeFxRate.includes(formik.values.transaction_type);
    const isBuy = listTypeBuy.includes(formik.values.transaction_type);
    const isShowSplit = listTypeWithSplitPostQuantity.includes(formik.values.transaction_type);
    const isBrokerCurrencyConversion =
      PortfolioTransactionType.BrokerCurrencyConversion === formik.values.transaction_type;
    const isBrokerDeposit =
      PortfolioTransactionType.BrokerDeposit === formik.values.transaction_type;
    const isBrokerWithdraw =
      PortfolioTransactionType.BrokerWithdraw === formik.values.transaction_type;
    const isSellType = listTypeWithSell.includes(formik.values.transaction_type);
    const isBuyType = listTypeWithBuy.includes(formik.values.transaction_type);
    const isCashInterest =
      PortfolioTransactionType.IncomeCashInterest === formik.values.transaction_type;

    return {
      isShowAmount,
      isShowQuantity,
      isShowPrice,
      isShowAsset,
      isShowToBank,
      isShowFromBank,
      isShowBroker,
      isShowBook,
      isShowFeeField,
      isRevaluation,
      isShowFxRate,
      isBuy,
      isShowSplit,
      isBrokerCurrencyConversion,
      isBrokerDeposit,
      isBrokerWithdraw,
      isSellType,
      isBuyType,
      isCashInterest,
    };
  }, [formik.values.transaction_type]);

  const { fromCurrency, toCurrency } = useMemo(() => {
    const fromCurrency = isBrokerCurrencyConversion
      ? formik.values.from_currency
      : isBuy
      ? currencyBank
      : currencyAsset;
    const toCurrency = isBrokerCurrencyConversion
      ? formik.values.to_currency
      : isBuy
      ? currencyAsset
      : currencyBank;

    return { fromCurrency, toCurrency };
  }, [
    currencyAsset,
    currencyBank,
    formik.values.from_currency,
    formik.values.to_currency,
    isBuy,
    isBrokerCurrencyConversion,
  ]);

  const { data: dataCurrencyHistoricalFxRate, isFetching: isFetchingCurrencyHistoricalFxRate } =
    useIssuerCurrencyHistoricalFxRateQuery(
      {
        from_currency: fromCurrency,
        to_currency: toCurrency,
        fx_date: dayjs(formik.values.confirmation_date).format('YYYY-MM-DD'),
      },
      !isShowFxRate ||
        !fromCurrency ||
        !toCurrency ||
        !formik.values.confirmation_date ||
        disableLoadRate,
    );

  const { onChangeTotal, onChangeQuantity, onChangePricePerShare, onChangeFee } =
    useQuantityPricePerShareTotal({
      formik,
      allowsFractionalSharesQuantity: true,
      addFeeToTotal: true,
      decimalPointsQuantity: 16,
      decimalPointsTotal: 4,
      updateTotalForTrailingDecimal: true,
    });

  const handleChangeQuantitySplit = useCallback(
    (quantity: string | number) => {
      formik.setFieldError('quantity', '');

      if (quantity === '') {
        formik.setFieldValue('quantity', '');
        formik.setFieldValue('price_per_share', '');
        return;
      }

      let value = String(quantity || '');

      //We remove all the characters, except for numbers, commas and points
      value = value.replace(/[^\d.,]/g, '');

      //We allow only one point
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }

      //Comma processing
      const hasDot = value.includes('.');
      if (hasDot) {
        // If there is a point, the commas are just deleted
        value = value.replace(/,/g, '');
      } else {
        // if there is no point, but there is one comma, replace it with a point
        const commas = value.match(/,/g)?.length || 0;
        if (commas === 1) {
          value = value.replace(',', '.');
        } else {
          // several commas - we delete everything
          value = value.replace(/,/g, '');
        }
      }

      const regex = /^[0-9.,]*$/;

      if (!regex.test(value)) {
        return;
      }

      value = value.replaceAll(',', '.').replaceAll('..', '.');
      if (value === '.') {
        return;
      }
      if (hasDot) {
        // const frac = value.split('.')?.[1] || '';
        if (
          value.endsWith('.') //|| frac.length <= decimalPointsQuantity
        ) {
          formik.setFieldValue('quantity', value);
          formik.setFieldValue('price_per_share', '');
          return;
        }
      }

      const quantityRounded = new Big(value).round(4, BigRoundMode.roundHalfUp);

      formik.setFieldValue('quantity', Number(quantityRounded.valueOf()));
      formik.setFieldValue(
        'price_per_share',
        quantityRounded.gt(0) && formik.values.total
          ? Number(
              new Big(formik.values.total)
                .div(quantityRounded)
                .round(4, BigRoundMode.roundHalfUp)
                .valueOf(),
            )
          : 0,
      );
    },
    [formik.values.total],
  );

  const handleChangePricePerShareSplit = useCallback(
    (pricePerShare: number | string) => {
      formik.setFieldError('price_per_share', '');
      formik.setFieldError('quantity', '');

      if (pricePerShare === '') {
        formik.setFieldValue('quantity', '');
        formik.setFieldValue('price_per_share', '');
        return;
      }

      let value = String(pricePerShare || '');

      //We remove all the characters, except for numbers, commas and points
      value = value.replace(/[^\d.,]/g, '');

      //We allow only one point
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }

      //Comma processing
      const hasDot = value.includes('.');
      if (hasDot) {
        // If there is a point, the commas are just deleted
        value = value.replace(/,/g, '');
      } else {
        // if there is no point, but there is one comma, replace it with a point
        const commas = value.match(/,/g)?.length || 0;
        if (commas === 1) {
          value = value.replace(',', '.');
        } else {
          // several commas - we delete everything
          value = value.replace(/,/g, '');
        }
      }

      const regex = /^[0-9.,]*$/;

      if (!regex.test(value)) {
        return;
      }

      value = value.replaceAll(',', '.').replaceAll('..', '.');
      if (value === '.') {
        return;
      }
      if (hasDot) {
        // const frac = value.split('.')?.[1] || '';
        if (
          value.endsWith('.') //|| frac.length <= decimalPointsQuantity
        ) {
          formik.setFieldValue('price_per_share', value);
          formik.setFieldValue('quantity', '');
          return;
        }
      }

      const pricePerShareRounded = new Big(value).round(4, BigRoundMode.roundHalfUp);

      formik.setFieldValue('price_per_share', pricePerShareRounded);
      formik.setFieldValue(
        'quantity',
        pricePerShareRounded.gt(0) && formik.values.total
          ? Number(
              new Big(formik.values.total)
                .div(pricePerShareRounded)
                .round(4, BigRoundMode.roundHalfUp)
                .valueOf(),
            )
          : 0,
      );
    },
    [formik.values.total],
  );

  const handleChangeTransactionType = useCallback((value: PortfolioTransactionType | null) => {
    // const prevValue = formik.values.transaction_type;
    // const prevType = !prevValue
    //   ? null
    //   : listTypeWithBuy.includes(prevValue)
    //   ? 'buy'
    //   : listTypeWithSell.includes(prevValue)
    //   ? 'sell'
    //   : null;
    // const currType = !value
    //   ? null
    //   : listTypeWithBuy.includes(value)
    //   ? 'buy'
    //   : listTypeWithSell.includes(value)
    //   ? 'sell'
    //   : null;

    const isShowAmount = value ? listTypeWithAmount.includes(value) : false;
    const isShowQuantity = value ? listTypeWithQuantity.includes(value) : false;
    const isShowPrice = value ? listTypeWithPrice.includes(value) : false;
    // const isShowAsset = value ? listTypeWithAsset.includes(value) : false;
    const isShowFxRate = value ? listTypeFxRate.includes(value) : false;
    // const isShowBroker = value ? listTypeWithBroker.includes(value) : false;
    // const isRevaluation = value ? value === PortfolioTransactionType.Revaluation : false;

    // if (prevType && currType && prevType !== currType) {
    formik.setFieldValue('from_portfolio_bank_id', undefined);
    formik.setFieldError('from_portfolio_bank_id', '');
    formik.setFieldValue('to_portfolio_bank_id', undefined);
    formik.setFieldError('to_portfolio_bank_id', '');
    formik.setFieldValue('ref_portfolio_transaction_id', undefined);
    formik.setFieldError('ref_portfolio_transaction_id', '');
    // }

    // if (currType && prevType !== currType) {
    formik.setFieldValue('total', isShowQuantity ? 0 : undefined);
    formik.setFieldError('total', '');
    formik.setFieldValue('quantity', isShowQuantity ? 0 : undefined);
    formik.setFieldError('quantity', '');
    formik.setFieldValue('price_per_share', isShowPrice ? 0 : undefined);
    formik.setFieldError('price_per_share', '');

    formik.setFieldValue('amount', isShowAmount ? 0 : undefined);
    formik.setFieldError('amount', '');
    formik.setFieldValue('fee', undefined);
    formik.setFieldError('fee', '');
    // }

    formik.setFieldValue('asset_id', undefined);
    formik.setFieldError('asset_id', '');
    setSelectedAsset(undefined);
    setCurrencyAsset(undefined);
    formik.setFieldValue('broker_id', undefined);
    formik.setFieldError('broker_id', '');
    setBrokerBalances([]);
    setCurrencyBank(undefined);
    setAvailableBank(undefined);

    formik.setFieldValue('to_currency', undefined);
    formik.setFieldError('to_currency', '');
    formik.setFieldValue('from_currency', undefined);
    formik.setFieldError('from_currency', '');

    if (!isShowFxRate) {
      formik.setFieldValue('fx_rate', undefined);
      formik.setFieldError('fx_rate', '');
    }

    formik.setFieldValue('transaction_type', value);
    formik.setFieldError('transaction_type', '');
  }, []);

  const handleChangeType = useCallback(
    (value: PortfolioTransactionTypeEdit | null) => {
      setType(value);
      if (value === 'buy') {
        handleChangeTransactionType(
          mapPortfolioTransactionEditTypeToType[value] || PortfolioTransactionType.TradeBuyBook,
        );
        return;
      }
      if (value === 'sell') {
        handleChangeTransactionType(
          mapPortfolioTransactionEditTypeToType[value] || PortfolioTransactionType.TradeSellBook,
        );
        return;
      }
      handleChangeTransactionType(
        value ? mapPortfolioTransactionEditTypeToType[value] ?? null : null,
      );
    },
    [handleChangeTransactionType],
  );

  // const handleChangeSplitType = useCallback(
  //   (value: SplitType | null) => {
  //     formik.setFieldValue('splitType', value);
  //     formik.setFieldError('splitType', '');
  //   },
  //   [handleChangeTransactionType],
  // );

  const handleChangeSelectAsset = useCallback(
    (item: SelectPaginateOption<AssetExtended> | null) => {
      setSelectedAsset(item?.value);
      formik.setFieldValue('asset_id', item?.value?.id || null);
      formik.setFieldError('asset_id', '');
      setCurrencyAsset(item?.value?.currency);
      setTimeout(() => {
        if (item?.value?.currency && item?.value?.currency === currencyBank) {
          formik.setFieldValue('fx_rate', 1);
          formik.setFieldError('fx_rate', '');
        }
        if ((isSellType || isBuyType) && !!formik.values.broker_id && item?.value?.currency) {
          //broker have many balances need recheck
          const currentBalanceWithAssetCurrency = brokerBalances.find(
            balance => balance.currency === item?.value?.currency,
          );
          if (!currentBalanceWithAssetCurrency) {
            formik.setFieldValue('broker_id', null);
            formik.setFieldError('broker_id', '');
          }
          setCurrencyBank(currentBalanceWithAssetCurrency?.currency || undefined);
          setAvailableBank(
            currentBalanceWithAssetCurrency?.value ? +currentBalanceWithAssetCurrency?.value : 0,
          );

          // formik.setFieldValue('to_currency', item?.value?.currency);
          // formik.setFieldError('to_currency', '');
          // formik.setFieldValue(
          //   'from_currency',
          //   currentBalanceWithAssetCurrency?.currency || item?.value?.currency,
          // );
          // formik.setFieldError('from_currency', '');
        }
      }, 1);

      if (isRevaluation) {
        const price_per_share = formatPricePerShareForInput(item?.value?.price_per_share ?? 0);
        formik.setFieldValue('price_per_share', price_per_share);
        formik.setFieldError('price_per_share', '');
        formik.setFieldValue('quantity', undefined);
        formik.setFieldError('quantity', '');
        formik.setFieldValue('total', undefined);
        formik.setFieldError('total', '');
        formik.setFieldValue('amount', undefined);
        formik.setFieldError('amount', '');
      } else if (isShowPrice) {
        const transactionType = formik.values.transaction_type;
        const type = listTypeWithBuy?.includes(transactionType)
          ? 'buy'
          : listTypeWithSell?.includes(transactionType)
          ? 'sell'
          : '';
        const isSplit = listTypeWithSplitPostQuantity.includes(transactionType);
        const quantity = type === 'sell' || isSplit ? item?.value?.quantity ?? 0 : 1;
        const price_per_share = formatPricePerShareForInput(item?.value?.price_per_share ?? 0);

        formik.setFieldValue('quantity', quantity);
        formik.setFieldError('quantity', '');
        formik.setFieldValue('price_per_share', price_per_share);
        formik.setFieldError('price_per_share', '');
        formik.setFieldValue(
          'total',
          price_per_share && quantity
            ? Number(
                new Big(quantity)
                  .mul(price_per_share)
                  .plus(formik.values.fee ? formik.values.fee : 0)
                  .round(2)
                  .valueOf(),
              )
            : 0,
        );
        formik.setFieldError('total', '');
      }
      formik.setFieldValue('ref_portfolio_transaction_id', undefined);
      formik.setFieldError('ref_portfolio_transaction_id', '');
    },
    [
      formik.values.transaction_type,
      formik.values.fee,
      isShowAmount,
      isShowPrice,
      isRevaluation,
      currencyBank,
      formik.values.broker_id,
      isBuyType,
      isSellType,
      brokerBalances,
    ],
  );

  const handleChangeSelectBroker = useCallback(
    (options: SelectPaginateOption<BrokerExtended> | null, field: string) => {
      formik.setFieldValue(field, options?.value?.id ?? null);
      formik.setFieldError(field, '');
      const brokerBalances = options?.value?.balances || [];
      setBrokerBalances(brokerBalances);
      formik.setFieldValue('from_currency', null);
      formik.setFieldError('from_currency', '');

      setTimeout(() => {
        if (isSellType || isBuyType) {
          formik.setFieldValue('from_portfolio_bank_id', null);
          formik.setFieldValue('to_portfolio_bank_id', null);
          formik.setFieldError('from_portfolio_bank_id', '');
          formik.setFieldError('to_portfolio_bank_id', '');
          const currentBalanceWithAssetCurrency =
            brokerBalances.find(item => item.currency === currencyAsset) || brokerBalances?.[0];
          setCurrencyBank(currentBalanceWithAssetCurrency?.currency || currencyAsset);
          setAvailableBank(
            currentBalanceWithAssetCurrency?.value ? +currentBalanceWithAssetCurrency?.value : 0,
          );
          // formik.setFieldValue('to_currency', currencyAsset);
          // formik.setFieldError('to_currency', '');
          // formik.setFieldValue(
          //   'from_currency',
          //   currentBalanceWithAssetCurrency?.currency || currencyAsset,
          // );
          // formik.setFieldError('from_currency', '');
        }
      }, 1);
    },
    [isBuyType, isSellType, currencyAsset],
  );

  const handleChangeSelectBank = useCallback(
    (fieldName: keyof CreatePortfolioTransactionRequestImprove) =>
      (item: SelectPaginateOption<RegisterBankExtended> | null) => {
        formik.setFieldValue(fieldName, item?.value.id);
        formik.setFieldError(fieldName, '');
        setCurrencyBank(item?.value?.bank_currency);
        setAvailableBank(
          item?.value?.total_available_cash ? +item?.value?.total_available_cash : undefined,
        );
        setTimeout(() => {
          if (currencyBank && currencyBank === currencyAsset) {
            formik.setFieldValue('fx_rate', 1);
            formik.setFieldError('fx_rate', '');
          }
          if (isCashInterest && fieldName === 'to_portfolio_bank_id') {
            setCurrencyAsset(item?.value?.bank_currency);
          }
          if (isSellType || isBuyType) {
            formik.setFieldValue('broker_id', null);
            formik.setFieldError('broker_id', '');
            // formik.setFieldValue('to_currency', null);
            // formik.setFieldError('to_currency', '');
            // formik.setFieldValue('from_currency', null);
            // formik.setFieldError('from_currency', '');
            setBrokerBalances([]);
          }
        }, 1);
      },
    [currencyAsset, currencyBank, isBuyType, isSellType, isCashInterest],
  );

  const handleChangeDate =
    (fieldName: keyof CreatePortfolioTransactionRequestImprove): CalendarInputProps['onChange'] =>
    date => {
      if (!date) {
        formik.setFieldValue(fieldName, '');
        formik.setFieldError(fieldName, '');
        return;
      }

      formik.setFieldValue(fieldName, date.toISOString());
      formik.setFieldError(fieldName, '');
    };

  const handleChangeSettle = (checked: boolean) => {
    setSettle(checked);
  };

  useEffect(() => {
    if (!ranRef.current && submitAfterOpen) {
      ranRef.current = true;
      formik.handleSubmit();
    }
  }, []);

  const onChangeNumber = (fieldName: string) => (amount: number | string) => {
    formik.setFieldValue(fieldName, amount);
    formik.setFieldError(fieldName, '');
  };

  const handleUpdateTotalBank = useCallback(
    (rate: number) => {
      const total = formik.values.total ?? formik.values.amount;
      const totalBank = !total
        ? 0
        : Number(rate) === 0
        ? isBrokerCurrencyConversion
          ? 0
          : total
        : isBrokerCurrencyConversion || isBuy
        ? Number(
            new Big(total)
              .div(rate || 1)
              .round(2)
              .valueOf(),
          )
        : Number(
            new Big(total)
              .mul(rate || 1)
              .round(2)
              .valueOf(),
          );

      formik.setFieldValue('totalBank', totalBank);
      formik.setFieldError('totalBank', '');
    },
    [formik.values.total, formik.values.amount, isBuy, isBrokerCurrencyConversion],
  );

  const onChangeFxRate = useCallback(
    (amount: number | string) => {
      formik.setFieldValue('fx_rate', amount);
      formik.setFieldError('fx_rate', '');
      const rate = isNumber(amount) && !isNaN(+amount) ? +amount : 0;
      handleUpdateTotalBank(rate);
    },
    [handleUpdateTotalBank],
  );

  const handleChangeSelect = useCallback(
    (field: string) => (option: string | boolean | null | any) => {
      formik.setFieldValue(field, option);
      formik.setFieldError(field, '');
      if (field === 'from_currency' && option) {
        setAvailableBank(brokerBalances?.find(item => item.currency === option)?.value || 0);
        if (formik.values.to_currency && (option as CurrencyEnum) === formik.values.to_currency) {
          formik.setFieldValue('to_currency', null);
          formik.setFieldError('to_currency', '');
        }
      }
    },
    [formik.values.to_currency, brokerBalances],
  );

  useEffect(() => {
    if (isShowFxRate && formik.values.fx_rate) {
      const rate = formik.values.fx_rate;
      handleUpdateTotalBank(rate);
    }
  }, [
    formik.values.total,
    formik.values.amount,
    formik.values.fx_rate,
    isShowFxRate,
    handleUpdateTotalBank,
  ]);

  useEffect(() => {
    if (isBrokerDeposit || isBrokerWithdraw) {
      const amount = formik.values.amount || 0;
      onChangeTotalBank(Number(new Big(amount).round(4).valueOf()));
    }
  }, [isBrokerDeposit, formik.values.amount, isBrokerWithdraw]);

  const onChangeTotalBank = (amount: number | string) => {
    formik.setFieldValue('totalBank', isNumber(amount) && !isNaN(+amount) ? +amount : '');
    formik.setFieldError('totalBank', '');
  };

  useEffect(() => {
    const price = dataCurrencyHistoricalFxRate?.data?.price;
    setIsFromFinancialModelingPrep(!!price);
    if (!disableLoadRate) {
      onChangeFxRate(price || '');
    }
  }, [dataCurrencyHistoricalFxRate]);

  const shouldShowRate =
    isShowFxRate &&
    !!toCurrency &&
    !!fromCurrency &&
    ((!!formik.values.asset_id &&
      (!!formik.values.to_portfolio_bank_id ||
        !!formik.values.from_portfolio_bank_id ||
        !!formik.values.broker_id)) ||
      isBrokerCurrencyConversion) &&
    !!formik.values.confirmation_date &&
    toCurrency !== fromCurrency;

  const shouldShowTotalBank =
    isShowFxRate &&
    !!formik.values.asset_id &&
    (!!formik.values.to_portfolio_bank_id ||
      !!formik.values.from_portfolio_bank_id ||
      !!formik.values.broker_id) &&
    toCurrency !== fromCurrency &&
    !!formik.values.confirmation_date;

  const shouldShowBalanceAfter =
    ((isShowFxRate &&
      !!formik.values.asset_id &&
      (!!formik.values.to_portfolio_bank_id ||
        !!formik.values.from_portfolio_bank_id ||
        !!formik.values.broker_id)) ||
      isBrokerDeposit ||
      isBrokerWithdraw ||
      (isBrokerCurrencyConversion && fromCurrency && toCurrency)) &&
    !!formik.values.confirmation_date;

  // Broker deposits withdraw cash from the selected bank; broker withdrawals
  // withdraw cash from the selected broker balance.
  const brokerBalanceForSelectedBankCurrency =
    brokerBalances.find(balance => balance.currency === currencyBank)?.value ?? 0;

  const canScheduleFuture = isBuyType || isSellType;
  const isEditingExistingTrade =
    !!item &&
    (listTypeWithBuy.includes(item.transaction_type) ||
      listTypeWithSell.includes(item.transaction_type));
  const getDisabledFutureDate = (date: Dayjs) => date.isAfter() && !date.isSame(dayjs(), 'day');

  return (
    <ModalContentWrapper
      onClose={onClose}
      open={open}
      title={
        item
          ? isSettle
            ? 'New Trade Settlement'
            : 'Update Portfolio Transaction'
          : 'New Portfolio Transaction'
      }
      classes={{ children: 'p-0' }}
    >
      <FormikProvider value={formik}>
        <div className="p-3 sm:p-6 flex flex-col w-full gap-6">
          <Select
            isClearable={true}
            disabled={isSettle || isEditingExistingTrade}
            label="Transaction Type"
            options={getSelectOptionByEnum<PortfolioTransactionTypeEdit>(
              PortfolioTransactionTypeEdit,
            ).map(item => ({
              ...item,
              label: item.label.replace('Dividend', 'Dividend /'),
            }))}
            value={type}
            onChange={handleChangeType}
            error={formik.errors.transaction_type}
          />

          {/* {isShowSplit && ( */}
          {/*   <Select */}
          {/*     isClearable={true} */}
          {/*     label="Split Type" */}
          {/*     options={getSelectOptionByEnum<SplitType>(SplitType)} */}
          {/*     value={formik.values.splitType} */}
          {/*     onChange={handleChangeSplitType} */}
          {/*     error={formik.errors.splitType} */}
          {/*   /> */}
          {/* )} */}

          {isShowAsset && (
            <SelectAsset
              disabled={isSettle || isEditingExistingTrade}
              portfolioId={portfolioId}
              onChange={handleChangeSelectAsset}
              value={formik.values.asset_id}
              label="Asset"
              showAddNewItem={true}
              clearable={true}
              withBrokers={false}
            />
          )}

          {!isSettle && isShowBook && (
            <CustomCheckbox
              name="is_complete_book"
              className="w-auto"
              label={'Mark as fully settled'}
              checked={settle}
              onChange={e => handleChangeSettle(e.target.checked)}
            />
          )}

          {isShowBroker && (
            <SelectPortfolioBrokers
              portfolioId={portfolioId}
              label={
                isBuyType
                  ? 'From broker balance (optional)'
                  : isSellType
                  ? 'To broker balance (optional)'
                  : 'Select Broker'
              }
              onChange={handleChangeSelectBroker}
              value={formik.values.broker_id}
              hideWithoutBalances={isBuyType || isBrokerWithdraw}
              hideWithoutCurrency={
                isBuyType ? currencyAsset : isBrokerWithdraw ? currencyBank : undefined
              }
              error={
                isBuyType
                  ? formik.errors.from_portfolio_bank_id
                  : isSellType
                  ? formik.errors.to_portfolio_bank_id
                  : formik.errors.broker_id
              }
              clearable={true}
              formFieldTitle="broker_id"
              placeholder="Select..."
              disabled={!formik.values.transaction_type || isSettle || isEditingExistingTrade}
              hintLabel={
                isBuyType && currencyAsset
                  ? `Brokers without balances and asset currency ${
                      currencyAsset?.toUpperCase() || ''
                    } are hidden`
                  : ''
              }
              balanceAdjustment={
                item?.broker_id === formik.values.broker_id ? brokerBalanceAdjustment : undefined
              }
              // warning={isBuyType || isSellType ? 'Choose a bank or a broker for the balance' : ''}
              date_to={
                formik.values.confirmation_date ? formik.values.confirmation_date : undefined
              }
            />
          )}

          {isShowFromBank && (
            <RegisterBanksSelect
              onChange={handleChangeSelectBank('from_portfolio_bank_id')}
              value={formik.values.from_portfolio_bank_id}
              label={'From Bank' + (isBuyType ? ' (optional)' : '')}
              clearable={true}
              error={formik.errors.from_portfolio_bank_id}
              // needBankType={BankTypeEnum.Portfolio}
              disabled={!formik.values.transaction_type || isSettle || isEditingExistingTrade}
              prefillBank={false}
              showAvailableCashInsteadOfTypes={true}
              onSelectedBankChange={handleSelectedFromBankChange}
              onOptionsMetaChange={({ isFetching }) => setIsFromBankBalanceFetching(isFetching)}
              date_to={
                formik.values.confirmation_date ? formik.values.confirmation_date : undefined
              }
              portfolio_id={portfolioId}
              is_overall={true}
            />
          )}

          {isShowToBank && (
            <RegisterBanksSelect
              onChange={handleChangeSelectBank('to_portfolio_bank_id')}
              value={formik.values.to_portfolio_bank_id}
              label={'To Bank' + (isSellType ? ' (optional)' : '')}
              clearable={true}
              error={formik.errors.to_portfolio_bank_id}
              // needBankType={BankTypeEnum.Portfolio}
              disabled={!formik.values.transaction_type || isSettle || isEditingExistingTrade}
              prefillBank={false}
              showAvailableCashInsteadOfTypes={true}
              date_to={
                formik.values.confirmation_date ? formik.values.confirmation_date : undefined
              }
              portfolio_id={portfolioId}
              is_overall={true}
            />
          )}

          <CalendarInput
            label="Date"
            name="confirmation_date"
            onChange={handleChangeDate('confirmation_date')}
            error={formik.errors.confirmation_date}
            value={formik.values.confirmation_date ? dayjs(formik.values.confirmation_date) : null}
            showTime={{ format: 'HH:mm' }}
            format="DD-MM-YYYY HH:mm"
            disabledDate={canScheduleFuture ? undefined : getDisabledFutureDate}
            disabledTime={canScheduleFuture ? undefined : date => getDisableTime(date, true)}
          />

          {isShowAmount && (
            <div className="w-full flex flex-wrap sm:flex-nowrap items-start gap-6">
              <CurrencyInput
                name="amount"
                label="Amount"
                value={formik.values.amount ?? ''}
                error={formik.errors.amount}
                errorOutside
                onChange={onChangeNumber('amount')}
                decimalPoints={4}
                currency={
                  isBrokerCurrencyConversion
                    ? toCurrency?.toUpperCase() || ''
                    : isBrokerDeposit || isBrokerWithdraw
                    ? currencyBank?.toUpperCase() || ''
                    : currencyAsset?.toUpperCase() || ''
                }
              />
              {isShowFeeField && (
                <CurrencyInput
                  name="fee"
                  label="Fee (optional)"
                  error={formik.errors.fee}
                  value={formik.values.fee ?? ''}
                  onChange={onChangeFee}
                  currency={currencyAsset?.toUpperCase() || ''}
                  decimalPoints={4}
                />
              )}
            </div>
          )}

          {(isShowPrice || isShowQuantity) && (
            <div className="w-full flex flex-wrap sm:flex-nowrap items-start gap-6">
              {isShowQuantity && (
                <CurrencyInput
                  currency=""
                  name="quantity"
                  label="quantity"
                  value={formik.values.quantity ?? ''}
                  error={formik.errors.quantity}
                  errorOutside
                  decimalPoints={16}
                  onChange={isShowSplit ? handleChangeQuantitySplit : onChangeQuantity}
                />
              )}
              {isShowPrice && (
                <CurrencyInput
                  name="price_per_share"
                  label="price"
                  value={formik.values.price_per_share ?? ''}
                  error={formik.errors.price_per_share}
                  errorOutside
                  onChange={isShowSplit ? handleChangePricePerShareSplit : onChangePricePerShare}
                  decimalPoints={6}
                  currency={currencyAsset?.toUpperCase() || ''}
                />
              )}
            </div>
          )}

          {isShowPrice && isShowQuantity && (
            <div className="w-full flex flex-wrap sm:flex-nowrap items-start gap-6">
              {isShowPrice && isShowQuantity && (
                <CurrencyInput
                  name="total"
                  label="total"
                  error={formik.errors.total}
                  value={formik.values.total ?? ''}
                  onChange={onChangeTotal}
                  currency={currencyAsset?.toUpperCase() || ''}
                  decimalPoints={2}
                  disabled={isShowSplit}
                />
              )}
              {isShowFeeField && (
                <CurrencyInput
                  name="fee"
                  label="Fee (optional)"
                  error={formik.errors.fee}
                  value={formik.values.fee ?? ''}
                  onChange={onChangeFee}
                  currency={currencyAsset?.toUpperCase() || ''}
                  decimalPoints={4}
                />
              )}
            </div>
          )}

          {isBrokerCurrencyConversion && (
            <div className="w-full flex flex-wrap sm:flex-nowrap items-start gap-6">
              <Select
                isClearable={true}
                value={formik.values.from_currency}
                placeholder={
                  !formik.values.broker_id
                    ? 'Broker not selected'
                    : brokerBalances.length === 0
                    ? 'The broker has no balance'
                    : 'Select from currency'
                }
                disabled={brokerBalances.length === 0 || !formik.values.broker_id}
                label="From currency"
                positionMenu="down"
                onChange={handleChangeSelect('from_currency')}
                options={brokerBalances.map(({ currency }) => ({
                  label: (
                    <span className="flex gap-4 items-center font-[Inter] text-sm leading-5">
                      <span className="text-gray-900">{currency.toUpperCase()}</span>
                    </span>
                  ),
                  value: currency,
                  toString: () => fixNameEnum(currency),
                }))}
                error={formik.errors.from_currency}
              />
              <Select
                isClearable={true}
                value={formik.values.to_currency}
                placeholder={
                  !formik.values.broker_id ? 'Broker not selected' : 'Select to currency'
                }
                disabled={!formik.values.broker_id}
                label="To currency"
                positionMenu="down"
                onChange={handleChangeSelect('to_currency')}
                options={Object.entries(CurrencyEnum)
                  .map(item => ({
                    label: (
                      <span className="flex gap-4 items-center font-[Inter] text-sm leading-5">
                        <span className="text-gray-900">{item[1].toUpperCase()}</span>
                      </span>
                    ),
                    value: item[1],
                    toString: () => fixNameEnum(item[0]),
                  }))
                  .filter(item => item.value !== formik.values.from_currency)}
                error={formik.errors.to_currency}
              />
            </div>
          )}

          {shouldShowRate && (
            <TooltipNew
              text={`${goodFormatWithCurrency(toCurrency?.toUpperCase())(
                formik.values.fx_rate || 0,
              )} = ${goodFormatWithCurrency(fromCurrency?.toUpperCase())(1)}`}
            >
              <CurrencyInput
                label={
                  <div className="flex items-center gap-4">
                    <span>
                      Exchange Rate
                      {isFromFinancialModelingPrep && (
                        <>
                          {' '}
                          <span className="font-bold font-italic">
                            provided by Financial Modeling Prep
                          </span>
                        </>
                      )}
                    </span>
                    {isFetchingCurrencyHistoricalFxRate && <Spinner size="xs" className="m-0" />}
                    {disableLoadRate && (
                      <span>
                        (
                        <span
                          className="cursor-pointer hover:underline text-blue-500"
                          onClick={() => setDisableLoadRate(false)}
                        >
                          Update
                        </span>
                        )
                      </span>
                    )}
                  </div>
                }
                decimalPoints={16}
                disabled={isFetchingCurrencyHistoricalFxRate}
                error={formik.errors.fx_rate}
                value={formik.values.fx_rate}
                onChange={price => {
                  onChangeFxRate(price);
                  setIsFromFinancialModelingPrep(false);
                }}
                currency={toCurrency?.toUpperCase() || ''}
              />
            </TooltipNew>
          )}

          {shouldShowTotalBank && (
            <CurrencyInput
              name="totalBank"
              label={`Total ${isBuy ? 'from' : 'to'} Bank`}
              error={formik.errors.totalBank}
              value={formik.values.totalBank ?? ''}
              onChange={
                isBrokerDeposit || isBrokerWithdraw
                  ? onChangeNumber('totalBank')
                  : onChangeTotalBank
              }
              currency={(isBuy ? fromCurrency?.toUpperCase() : toCurrency?.toUpperCase()) || ''}
              decimalPoints={2}
              disabled={true}
            />
          )}
          <TextInputField textarea name="memo" label="Memo (Optional)" limit={1024} />

          {isBrokerDeposit &&
            isNumber(availableBank) &&
            isNumber(formik.values.totalBank) &&
            !isNaN(availableBank) &&
            !isNaN(formik.values.totalBank) && (
              <div className="w-full">
                <span className="font-[Inter] text-[10px] leading-[18px] text-gray-900 text-opacity-[.64] uppercase">
                  Bank balance after
                </span>
                <span className="flex items-baseline text-nowrap gap-1">
                  <span className="text-xl">
                    {goodFormatNumber(availableBank - formik.values.totalBank)}
                  </span>
                  <span className="text-xs text-gray-500">{currencyBank?.toUpperCase()}</span>
                </span>
              </div>
            )}

          {!isBrokerDeposit &&
            isBrokerWithdraw &&
            isNumber(formik.values.totalBank) &&
            !isNaN(formik.values.totalBank) && (
              <div className="w-full">
                <span className="font-[Inter] text-[10px] leading-[18px] text-gray-900 text-opacity-[.64] uppercase">
                  Broker balance after
                </span>
                <span className="flex items-baseline text-nowrap gap-1">
                  <span className="text-xl">
                    {goodFormatNumber(
                      +brokerBalanceForSelectedBankCurrency - formik.values.totalBank,
                    )}
                  </span>
                  <span className="text-xs text-gray-500">{currencyBank?.toUpperCase()}</span>
                </span>
              </div>
            )}

          {!isBrokerDeposit &&
            !isBrokerWithdraw &&
            shouldShowBalanceAfter &&
            isNumber(availableBank) &&
            isNumber(formik.values.totalBank) &&
            !isNaN(availableBank) &&
            !isNaN(formik.values.totalBank) && (
              <div className="w-full">
                <span className="font-[Inter] text-[10px] leading-[18px] text-gray-900 text-opacity-[.64] uppercase">
                  {formik.values.broker_id
                    ? isBrokerCurrencyConversion
                      ? `Broker's ${fromCurrency?.toUpperCase()} Balance after`
                      : 'Broker balance after'
                    : 'Bank balance after'}
                </span>
                <span className="flex items-baseline text-nowrap gap-1">
                  <span className="text-xl">
                    {isBrokerCurrencyConversion || isBuy
                      ? goodFormatNumber(availableBank - formik.values.totalBank)
                      : goodFormatNumber(availableBank + formik.values.totalBank)}
                  </span>
                  {(!!fromCurrency || !!toCurrency) && (
                    <span className="text-xs text-gray-500">
                      {isBrokerCurrencyConversion || isBuy
                        ? fromCurrency?.toUpperCase()
                        : toCurrency?.toUpperCase()}
                    </span>
                  )}
                </span>
              </div>
            )}
        </div>
        <div className="mt-4 p-3 !pb-0 sm:p-6">
          <Button
            type="submit"
            onClick={formik.handleSubmit}
            loading={isLoadingAdd || isLoadingEdit}
          >
            {item ? 'Submit' : 'Add new transaction'}
          </Button>
        </div>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
